import React, { useState, useMemo } from 'react';
import { useData } from '../context/DataContext';
import { usePlus } from '../context/PlusContext';
import { X, CalendarClock } from 'lucide-react';
import { Periodicidad, TransaccionRecurrente } from '../types';
import { aNumero, AVISO_IMPORTE, PATRON_IMPORTE_POSITIVO } from '../utils/numeros';
import { useCerrarConEscape } from '../hooks/useCerrarConEscape';

interface Props { onClose: () => void; transaccionEditar?: TransaccionRecurrente; }

const PERIODICIDADES: { v: Periodicidad; l: string }[] = [
  { v: 'diario',      l: 'Diario'       },
  { v: 'laboral',     l: 'Día laboral'  },
  { v: 'semanal',     l: 'Semanal'      },
  { v: 'mensual',     l: 'Mensual'      },
  { v: 'trimestral',  l: 'Trimestral'   },
  { v: 'semestral',   l: 'Semestral'    },
  { v: 'anual',       l: 'Anual'        },
];

// 0=Domingo … 6=Sábado (coincide con Date.getDay())
const DIAS_SEMANA: { v: number; l: string }[] = [
  { v: 1, l: 'Lunes'     },
  { v: 2, l: 'Martes'    },
  { v: 3, l: 'Miércoles' },
  { v: 4, l: 'Jueves'    },
  { v: 5, l: 'Viernes'   },
  { v: 6, l: 'Sábado'    },
  { v: 0, l: 'Domingo'   },
];

// Devuelve la primera fecha >= desde cuyo día de la semana sea diaSemana
const proximoDiaSemana = (desde: Date, diaSemana: number): Date => {
  const d = new Date(desde);
  d.setHours(0, 0, 0, 0);
  while (d.getDay() !== diaSemana) d.setDate(d.getDate() + 1);
  return d;
};

export const ModalTransaccionRecurrente: React.FC<Props> = ({ onClose, transaccionEditar }) => {
  useCerrarConEscape(onClose);
  const { crearTransaccionRecurrente, actualizarTransaccionRecurrente, cuentas, categorias, presupuestos } = useData();
  const { ofrecerSiEsLimite } = usePlus();
  const modoEdicion = !!transaccionEditar;

  const [nombre, setNombre]           = useState(transaccionEditar?.nombre || '');
  const [tipo, setTipo]               = useState<'ingreso' | 'gasto'>(transaccionEditar?.tipo || 'gasto');
  const [cantidad, setCantidad]       = useState(transaccionEditar?.cantidad.toString() || '');
  const [categoriaId, setCategoriaId] = useState(transaccionEditar?.categoriaId || '');
  const [cuentaId, setCuentaId]       = useState(transaccionEditar?.cuentaId || '');
  const [descripcion, setDescripcion] = useState(transaccionEditar?.descripcion || '');
  const [periodicidad, setPeriod]     = useState<Periodicidad>(transaccionEditar?.periodicidad || 'mensual');
  const [diaSemana, setDiaSemana]     = useState<number>(
    transaccionEditar?.diaSemana ?? new Date(transaccionEditar?.proximaFecha || Date.now()).getDay()
  );
  const [proximaFecha, setProxima]    = useState(
    transaccionEditar ? new Date(transaccionEditar.proximaFecha).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
  );
  const [presupuestoId, setPresupuesto] = useState(transaccionEditar?.presupuestoId || '');
  const [tieneFechaFin, setTieneFechaFin] = useState(!!transaccionEditar?.fechaFin);
  const [fechaFin, setFechaFin]           = useState(
    transaccionEditar?.fechaFin ? new Date(transaccionEditar.fechaFin).toISOString().split('T')[0] : ''
  );
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');

  const inputClass = "w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white";
  const labelClass = "block text-gray-700 dark:text-gray-300 font-semibold mb-2";

  const categoriasFiltradas = categorias.filter(c => c.tipo === tipo);

  const presupuestosDisponibles = useMemo(() => {
    if (tipo !== 'gasto' || !categoriaId) return [];
    // Usamos proximaFecha como referencia de período, igual que en el modal
    // de transacciones usa la fecha de la transacción.
    const fechaRef = new Date(proximaFecha);
    const mesTx    = `${fechaRef.getFullYear()}-${String(fechaRef.getMonth() + 1).padStart(2, '0')}`;
    const anioTx   = String(fechaRef.getFullYear());

    return presupuestos.filter(p => {
      if (p.categoriaId !== categoriaId) return false;
      if (modoEdicion) return true;
      if (p.periodicidad === 'mensual') return p.mes === mesTx;
      if (p.periodicidad === 'anual')   return p.mes === anioTx;
      return false;
    });
  }, [tipo, categoriaId, presupuestos, proximaFecha, modoEdicion]);

  const handleChangeTipo = (t: 'ingreso' | 'gasto') => {
    setTipo(t);
    setCategoriaId('');
    setPresupuesto('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!nombre || !cantidad || !categoriaId || !cuentaId) {
      setError('Completa todos los campos obligatorios'); return;
    }
    setLoading(true);
    try {
      // Para periodicidad semanal, la primera ejecución se ajusta al día de la
      // semana elegido (la primera fecha >= la indicada que caiga en ese día).
      const fechaInicio = periodicidad === 'semanal'
        ? proximoDiaSemana(new Date(proximaFecha), diaSemana)
        : new Date(proximaFecha);

      const datos = {
        nombre, tipo, cantidad: aNumero(cantidad),
        categoriaId, cuentaId, descripcion, periodicidad,
        proximaFecha: fechaInicio,
        ...(periodicidad === 'semanal' ? { diaSemana } : {}),
        ...(tieneFechaFin && fechaFin ? { fechaFin: new Date(fechaFin) } : {}),
        ...(presupuestoId ? { presupuestoId } : {}),
      };
      if (modoEdicion && transaccionEditar) {
        await actualizarTransaccionRecurrente(transaccionEditar.id, datos);
      } else {
        await crearTransaccionRecurrente({ ...datos, activo: true });
      }
      onClose();
    } catch (err: any) {
      // Sin hueco en el plan gratis: en vez del error, la hoja de Plus
      if (ofrecerSiEsLimite(err)) { onClose(); return; }
      setError(err.message || 'Error al guardar');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center capa-modal z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md max-h-full overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {modoEdicion ? 'Editar Transacción Periódica' : 'Nueva Transacción Periódica'}
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="boton-cerrar text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}

          <div>
            <label className={labelClass}>Nombre</label>
            <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
              className={inputClass} placeholder="Ej: Cuota gimnasio" required />
          </div>

          {/* Tipo */}
          <div>
            <label className={labelClass}>Tipo</label>
            <div className="flex gap-2">
              <button type="button" onClick={() => handleChangeTipo('gasto')}
                className={`flex-1 py-2 rounded-lg font-semibold transition text-sm ${
                  tipo === 'gasto' ? 'bg-red-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}>
                Gasto
              </button>
              <button type="button" onClick={() => handleChangeTipo('ingreso')}
                className={`flex-1 py-2 rounded-lg font-semibold transition text-sm ${
                  tipo === 'ingreso' ? 'bg-green-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}>
                Ingreso
              </button>
            </div>
          </div>

          <div>
            <label className={labelClass}>Importe (€)</label>
            <input type="text" inputMode="decimal" pattern={PATRON_IMPORTE_POSITIVO} title={AVISO_IMPORTE} value={cantidad}
              onChange={e => setCantidad(e.target.value)}
              className={inputClass} placeholder="0,00" required />
          </div>

          <div>
            <label className={labelClass}>Categoría</label>
            <select value={categoriaId}
              onChange={e => { setCategoriaId(e.target.value); setPresupuesto(''); }}
              className={inputClass} required>
              <option value="">Selecciona una categoría</option>
              {categoriasFiltradas.map(c => (
                <option key={c.id} value={c.id}>{c.icono} {c.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelClass}>Cuenta</label>
            <select value={cuentaId} onChange={e => setCuentaId(e.target.value)}
              className={inputClass} required>
              <option value="">Selecciona una cuenta</option>
              {cuentas.map(c => (
                <option key={c.id} value={c.id}>{c.nombre} — €{c.saldo.toFixed(2)}</option>
              ))}
            </select>
          </div>

          {presupuestosDisponibles.length > 0 && (
            <div>
              <label className={labelClass}>
                Imputar a presupuesto
                <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">(opcional)</span>
              </label>
              <select value={presupuestoId} onChange={e => setPresupuesto(e.target.value)}
                className={inputClass}>
                <option value="">Sin presupuesto específico</option>
                {presupuestosDisponibles.map(p => (
                  <option key={p.id} value={p.id}>{p.nombre} — límite €{p.limite.toFixed(2)}</option>
                ))}
              </select>
            </div>
          )}

          {/* Periodicidad */}
          <div>
            <label className={labelClass}>Periodicidad</label>
            <div className="grid grid-cols-3 gap-2">
              {PERIODICIDADES.map(({ v, l }) => (
                <button key={v} type="button" onClick={() => setPeriod(v)}
                  className={`py-2 rounded-lg text-sm font-semibold transition ${
                    periodicidad === v
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Día de la semana (solo semanal) */}
          {periodicidad === 'semanal' && (
            <div>
              <label className={labelClass}>Día de la semana</label>
              <div className="grid grid-cols-4 gap-2">
                {DIAS_SEMANA.map(({ v, l }) => (
                  <button key={v} type="button" onClick={() => setDiaSemana(v)}
                    className={`py-2 rounded-lg text-sm font-semibold transition ${
                      diaSemana === v
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}>
                    {l}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                La transacción se repetirá cada {DIAS_SEMANA.find(d => d.v === diaSemana)?.l.toLowerCase()}.
              </p>
            </div>
          )}

          <div>
            <label className={labelClass}>Primera ejecución</label>
            <input type="date" value={proximaFecha} onChange={e => setProxima(e.target.value)}
              className={inputClass} required />
          </div>

          {/* Fecha fin */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer mb-2">
              <input type="checkbox" checked={tieneFechaFin}
                onChange={e => { setTieneFechaFin(e.target.checked); if (!e.target.checked) setFechaFin(''); }}
                className="w-4 h-4 text-blue-600 rounded" />
              <span className="text-gray-700 dark:text-gray-300 font-semibold">Fecha de fin</span>
            </label>
            {tieneFechaFin && (
              <input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)}
                className={inputClass} required={tieneFechaFin} />
            )}
            {!tieneFechaFin && (
              <p className="text-xs text-gray-500 dark:text-gray-400">Sin fecha fin — se repite indefinidamente</p>
            )}
          </div>

          <div>
            <label className={labelClass}>Descripción (opcional)</label>
            <input type="text" value={descripcion} onChange={e => setDescripcion(e.target.value)}
              className={inputClass} placeholder="Notas adicionales" />
          </div>

          <div className="flex gap-4 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              Cancelar
            </button>
            <button type="submit" disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition disabled:opacity-50">
              {loading ? 'Guardando…' : modoEdicion ? 'Actualizar' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
